"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.dataService = void 0;
const firebaseBase_1 = require("./firebaseBase");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
const movieUtils_1 = require("../utils/movieUtils");
const constants_1 = require("../constants");
/**
 * Data Service (Cross-Platform)
 * Handles recent movies, search history, and data management
 * Platform-agnostic version with no localStorage dependencies
 */
class DataService {
    // =============== SEARCH HISTORY MANAGEMENT ===============
    /**
     * Save recent search
     */
    saveRecentSearch(user, searchQuery, searchType, resultCount) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                // Use unified service for cross-platform compatibility
                const existingDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userSearchHistory', user.uid);
                // Use Timestamp.now() instead of serverTimestamp() because serverTimestamp()
                // is not supported inside arrays in Firestore
                const newSearch = {
                    query: searchQuery.trim(),
                    searchType: searchType,
                    resultCount,
                    timestamp: firebaseBase_1.Timestamp.now(),
                };
                if (existingDoc.exists()) {
                    const currentData = existingDoc.data();
                    // Remove if already exists to avoid duplicates
                    const filteredSearches = (currentData.recentSearches || [])
                        .filter(search => search.query.toLowerCase() !== searchQuery.toLowerCase().trim());
                    // Add new search at the beginning and limit to 10 searches
                    const updatedSearches = [Object.assign({ id: `search_${Date.now()}` }, newSearch), ...filteredSearches].slice(0, 10);
                    // Update search stats
                    const currentStats = currentData.searchStats || {
                        totalSearches: 0,
                        movieSearches: 0,
                        personSearches: 0,
                        userSearches: 0,
                    };
                    yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userSearchHistory', user.uid, {
                        recentSearches: updatedSearches,
                        searchStats: {
                            totalSearches: currentStats.totalSearches + 1,
                            movieSearches: currentStats.movieSearches + (searchType === 'movie' ? 1 : 0),
                            personSearches: currentStats.personSearches + (searchType === 'person' ? 1 : 0),
                            userSearches: currentStats.userSearches + (searchType === 'user' ? 1 : 0),
                        },
                        updatedAt: new Date().toISOString()
                    });
                }
                else {
                    // Create new document
                    const newSearchHistory = {
                        recentSearches: [Object.assign({ id: `search_${Date.now()}` }, newSearch)],
                        popularSearches: [],
                        searchStats: {
                            totalSearches: 1,
                            movieSearches: searchType === 'movie' ? 1 : 0,
                            personSearches: searchType === 'person' ? 1 : 0,
                            userSearches: searchType === 'user' ? 1 : 0,
                        },
                        updatedAt: new Date().toISOString(),
                    };
                    yield (0, unifiedFirestoreService_1.unifiedSetDoc)('userSearchHistory', user.uid, Object.assign({ uid: user.uid }, newSearchHistory));
                }
                console.log('DataService: Saved recent search:', searchQuery);
            }), 'saveRecentSearch');
        });
    }
    /**
     * Get user's search history
     */
    getSearchHistory(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userSearchHistory', user.uid);
                if (!docSnap.exists()) {
                    console.log('DataService: No search history found for user');
                    return [];
                }
                const data = docSnap.data();
                // Sort by search time (most recent first)
                const recentSearches = (data.recentSearches || [])
                    .sort((a, b) => {
                    const aTime = a.timestamp instanceof firebaseBase_1.Timestamp ? a.timestamp.toDate().getTime() : 0;
                    const bTime = b.timestamp instanceof firebaseBase_1.Timestamp ? b.timestamp.toDate().getTime() : 0;
                    return bTime - aTime;
                });
                console.log('DataService: Retrieved search history:', recentSearches.length);
                return recentSearches;
            }), 'getSearchHistory');
        });
    }
    /**
     * Clear search history
     */
    clearSearchHistory(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userSearchHistory', user.uid, {
                    recentSearches: [],
                    updatedAt: new Date().toISOString()
                });
                console.log('DataService: Cleared search history for user');
            }), 'clearSearchHistory');
        });
    }
    /**
     * Delete specific search from history
     */
    deleteSearchFromHistory(user, searchQuery) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const existingDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userSearchHistory', user.uid);
                if (existingDoc.exists()) {
                    const currentData = existingDoc.data();
                    const filteredSearches = (currentData.recentSearches || [])
                        .filter(search => search.query.toLowerCase() !== searchQuery.toLowerCase());
                    yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userSearchHistory', user.uid, {
                        recentSearches: filteredSearches,
                        updatedAt: new Date().toISOString()
                    });
                }
                console.log('DataService: Deleted search from history:', searchQuery);
            }), 'deleteSearchFromHistory');
        });
    }
    // =============== RECENT MOVIES MANAGEMENT ===============
    /**
     * Save recent movie
     */
    saveRecentMovie(user, movieData) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const movieId = (0, movieUtils_1.getStandardizedMovieId)(movieData) || `tmdb_${movieData.id}`;
                const now = new Date().toISOString();
                const existingDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userRecentMovies', user.uid);
                const newMovieEntry = {
                    movieId,
                    movieData: Object.assign(Object.assign({}, movieData), { 
                        // Ensure we have proper poster handling
                        Poster: movieData.Poster || (movieData.poster_path ? `${constants_1.TMDB_IMAGE_BASE_URL}${movieData.poster_path}` : 'N/A'), poster_path: movieData.poster_path }),
                    accessedAt: now,
                };
                if (existingDoc.exists()) {
                    const currentData = existingDoc.data();
                    // Remove if already exists to avoid duplicates
                    const filteredMovies = (currentData.recentMovies || [])
                        .filter((movie) => movie.movieId !== movieId);
                    // Add new movie at the beginning and limit to max movies
                    const maxMovies = currentData.maxMovies || 10;
                    const updatedMovies = [newMovieEntry, ...filteredMovies].slice(0, maxMovies);
                    yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userRecentMovies', user.uid, {
                        recentMovies: updatedMovies,
                        updatedAt: now
                    });
                }
                else {
                    // Create new document
                    const newRecentMovies = {
                        recentMovies: [newMovieEntry],
                        maxMovies: 10,
                        updatedAt: now,
                    };
                    yield (0, unifiedFirestoreService_1.unifiedSetDoc)('userRecentMovies', user.uid, Object.assign({ uid: user.uid }, newRecentMovies));
                }
                console.log('DataService: Saved recent movie:', movieData.Title || movieData.title);
            }), 'saveRecentMovie');
        });
    }
    /**
     * Get user's recent movies
     */
    getRecentMovies(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userRecentMovies', user.uid);
                if (!docSnap.exists()) {
                    console.log('DataService: No recent movies found for user');
                    return [];
                }
                const data = docSnap.data();
                // Sort by access time (most recent first) and return movie data
                const recentMovies = (data.recentMovies || [])
                    .sort((a, b) => {
                    const aTime = a.accessedAt instanceof firebaseBase_1.Timestamp ? a.accessedAt.toDate().getTime() :
                        (typeof a.accessedAt === 'string' ? new Date(a.accessedAt).getTime() : 0);
                    const bTime = b.accessedAt instanceof firebaseBase_1.Timestamp ? b.accessedAt.toDate().getTime() :
                        (typeof b.accessedAt === 'string' ? new Date(b.accessedAt).getTime() : 0);
                    return bTime - aTime;
                })
                    .map((entry) => entry.movieData);
                console.log('DataService: Retrieved recent movies:', recentMovies.length);
                return recentMovies;
            }), 'getRecentMovies');
        });
    }
    /**
     * Clear recent movies
     */
    clearRecentMovies(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userRecentMovies', user.uid, {
                    recentMovies: [],
                    updatedAt: new Date().toISOString()
                });
                console.log('DataService: Cleared recent movies for user');
            }), 'clearRecentMovies');
        });
    }
    /**
     * Remove specific movie from recent movies
     */
    removeRecentMovie(user, movieId) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const existingDoc = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userRecentMovies', user.uid);
                if (existingDoc.exists()) {
                    const currentData = existingDoc.data();
                    const filteredMovies = (currentData.recentMovies || [])
                        .filter((movie) => movie.movieId !== movieId);
                    yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('userRecentMovies', user.uid, {
                        recentMovies: filteredMovies,
                        updatedAt: new Date().toISOString()
                    });
                }
                console.log('DataService: Removed recent movie:', movieId);
            }), 'removeRecentMovie');
        });
    }
    // =============== RECENTLY ADDED MOVIES MANAGEMENT ===============
    /**
     * Save recently added movies from TMDb API
     */
    saveRecentlyAddedMovies(user, movies) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const now = new Date().toISOString();
                const recentlyAddedData = {
                    recentlyAddedMovies: movies.map((movie) => ({
                        movieId: (0, movieUtils_1.getStandardizedMovieId)(movie) || `tmdb_${movie.id}`,
                        movieData: Object.assign(Object.assign({}, movie), { 
                            // Ensure we have proper poster handling
                            Poster: movie.Poster || (movie.poster_path ? `${constants_1.TMDB_IMAGE_BASE_URL}${movie.poster_path}` : 'N/A'), poster_path: movie.poster_path }),
                        addedAt: now,
                    })),
                    lastRefresh: now,
                    updatedAt: now,
                };
                yield (0, unifiedFirestoreService_1.unifiedSetDoc)('userRecentlyAddedMovies', user.uid, Object.assign({ uid: user.uid }, recentlyAddedData));
                console.log('DataService: Saved recently added movies for user:', user.uid, movies.length, 'movies');
            }), 'saveRecentlyAddedMovies');
        });
    }
    /**
     * Get recently added movies
     */
    getRecentlyAddedMovies(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userRecentlyAddedMovies', user.uid);
                if (!docSnap.exists()) {
                    console.log('DataService: No recently added movies found for user');
                    return [];
                }
                const data = docSnap.data();
                // Sort by added time (most recent first) and return movie data
                const recentlyAddedMovies = (data.recentlyAddedMovies || [])
                    .sort((a, b) => {
                    const aTime = a.addedAt instanceof firebaseBase_1.Timestamp ? a.addedAt.toDate().getTime() :
                        (typeof a.addedAt === 'string' ? new Date(a.addedAt).getTime() : 0);
                    const bTime = b.addedAt instanceof firebaseBase_1.Timestamp ? b.addedAt.toDate().getTime() :
                        (typeof b.addedAt === 'string' ? new Date(b.addedAt).getTime() : 0);
                    return bTime - aTime;
                })
                    .map((entry) => entry.movieData);
                console.log('DataService: Retrieved recently added movies:', recentlyAddedMovies.length);
                return recentlyAddedMovies;
            }), 'getRecentlyAddedMovies');
        });
    }
    /**
     * Check if recently added movies need refresh (older than 1 hour)
     */
    shouldRefreshRecentlyAddedMovies(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const docSnap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('userRecentlyAddedMovies', user.uid);
                if (!docSnap.exists()) {
                    return true; // No data exists, should refresh
                }
                const data = docSnap.data();
                const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
                const lastRefresh = data.lastRefresh instanceof firebaseBase_1.Timestamp ? data.lastRefresh.toDate() : new Date(data.lastRefresh);
                return lastRefresh < oneHourAgo;
            }), 'shouldRefreshRecentlyAddedMovies');
        });
    }
    // =============== USER ACTIVITY MANAGEMENT ===============
    /**
     * Create user activity
     */
    createUserActivity(user, type, metadata) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                console.log(`🎯 DataService: Creating activity for user ${user.uid}, type: ${type}`, metadata);
                // Build activity data with only defined values
                const activityData = {
                    uid: user.uid,
                    type: type,
                    isPrivate: metadata.isPrivate || false,
                    hiddenFromCrew: metadata.hiddenFromCrew || false,
                    createdAt: new Date().toISOString(),
                };
                // Only add fields that have values
                if (metadata.movieId)
                    activityData.movieId = metadata.movieId;
                if (metadata.listId)
                    activityData.watchlistId = metadata.listId;
                if (metadata.rating !== undefined && metadata.rating !== null)
                    activityData.rating = metadata.rating;
                // Build metadata object with only defined values
                const metadataObj = {};
                if (metadata.movieTitle)
                    metadataObj.movieTitle = metadata.movieTitle;
                if (metadata.moviePoster)
                    metadataObj.moviePoster = metadata.moviePoster;
                if (metadata.listName)
                    metadataObj.listName = metadata.listName;
                // Add any additional metadata fields that aren't undefined
                Object.entries(metadata).forEach(([key, value]) => {
                    if (value !== undefined && value !== null && !['movieId', 'listId', 'rating', 'isPrivate', 'hiddenFromCrew', 'movieTitle', 'moviePoster', 'listName'].includes(key)) {
                        metadataObj[key] = value;
                    }
                });
                // Only add metadata if it has content
                if (Object.keys(metadataObj).length > 0) {
                    activityData.metadata = metadataObj;
                }
                const docId = yield (0, unifiedFirestoreService_1.unifiedAddDoc)('userActivity', activityData);
                console.log('✅ DataService: Created user activity:', type, 'with ID:', docId, 'Data:', activityData);
            }), 'createUserActivity');
        });
    }
    /**
     * Get user activities
     */
    getUserActivities(user, limitCount = 20) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const querySnapshot = yield (0, unifiedFirestoreService_1.unifiedCollection)('userActivity')
                    .where('uid', '==', user.uid)
                    .orderBy('createdAt', 'desc')
                    .limit(limitCount)
                    .getDocs();
                return querySnapshot.map((docData) => (Object.assign({ id: docData.id }, docData.data())));
            }), 'getUserActivities');
        });
    }
    // =============== DATA CLEANUP AND MIGRATION ===============
    /**
     * Clear all user data (for privacy/deletion purposes)
     */
    clearAllUserData(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const clearOperations = [
                    this.clearSearchHistory(user),
                    this.clearRecentMovies(user),
                    // Note: Add other clear operations as needed
                ];
                yield Promise.all(clearOperations);
                console.log('DataService: Cleared all user data');
            }), 'clearAllUserData');
        });
    }
    /**
     * Get data summary for user (for debugging/analytics)
     */
    getUserDataSummary(user) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                const [searchHistory, recentMovies, recentlyAddedMovies, activities] = yield Promise.all([
                    this.getSearchHistory(user),
                    this.getRecentMovies(user),
                    this.getRecentlyAddedMovies(user),
                    this.getUserActivities(user, 100) // Get more for counting
                ]);
                return {
                    searchHistoryCount: searchHistory.length,
                    recentMoviesCount: recentMovies.length,
                    recentlyAddedMoviesCount: recentlyAddedMovies.length,
                    activitiesCount: activities.length
                };
            }), 'getUserDataSummary');
        });
    }
    /**
     * Delete all user data from Firestore (comprehensive deletion)
     */
    deleteUserData(uid) {
        return __awaiter(this, void 0, void 0, function* () {
            return (0, firebaseBase_1.safeCollectionOperation)(() => __awaiter(this, void 0, void 0, function* () {
                console.log('DataService: deleteUserData called for uid:', uid);
                // Use the comprehensive deletion method
                yield this.deleteAllUserData(uid);
                console.log('DataService: deleteUserData completed successfully for uid:', uid);
            }), 'deleteUserData');
        });
    }
    /**
     * Delete all user data from Firestore collections (private helper)
     */
    deleteAllUserData(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            console.log('DataService: Deleting all data for user:', userId);
            const singleDocCollections = [
                'userPreferences',
                'userSearchHistory',
                'userRecentMovies',
                'userRecentlyAddedMovies',
            ];
            for (const collectionName of singleDocCollections) {
                console.log(`DataService: Cleaning collection: ${collectionName}`);
                try {
                    yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)(collectionName, userId);
                }
                catch (_) { }
            }
            const multiDocCollections = [
                { name: 'userMovies', field: 'uid' },
                { name: 'watchlists', field: 'createdBy' },
                { name: 'userActivity', field: 'uid' },
                { name: 'userProfiles', field: 'uid' },
                { name: 'movieReviews', field: 'uid' },
            ];
            for (const { name: collectionName, field } of multiDocCollections) {
                console.log(`DataService: Cleaning collection: ${collectionName}`);
                const docs = yield (0, unifiedFirestoreService_1.unifiedCollection)(collectionName).where(field, '==', userId).getDocs();
                console.log(`DataService: Found ${docs.length} documents in ${collectionName}`);
                for (const d of docs) {
                    try {
                        yield (0, unifiedFirestoreService_1.unifiedDeleteDoc)(collectionName, d.id);
                    }
                    catch (_) { }
                }
            }
            console.log('DataService: Successfully deleted all user data');
        });
    }
}
// Export singleton instance
exports.dataService = new DataService();
exports.default = exports.dataService;
